import { IconInfoCircle } from "@tabler/icons-react"
import { Button } from "design-system/atoms/Button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Label } from "design-system/atoms/Label"
import { Textarea } from "design-system/atoms/Textarea"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { renderLiquid, whatsappLanguages } from "lopermut"
import { Braces, PencilIcon } from "lucide-react"
import { WhatsappTemplatePreview } from "modules/campaigns/components/Sections/channels/whatsapp/WhatsappTemplatePreview"
import { VaultVariableAutocomplete } from "modules/shared/components/VaultVariableAutocomplete"
import { useVaultPreviewContext } from "modules/shared/hooks/useVaultPreviewContext"
import { useEffect, useMemo, useRef, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import {
  CAMPAIGN_ACTION_WEBHOOK_DEFAULT_PAYLOAD,
  ChannelEnum,
  UpdateCampaignSchema,
  WorkflowActionInConversationMessageConfigSchema,
} from "schema"
import z from "zod"
import { useWorkflowBuilder } from "../../hooks/useWorkflowBuilder"

type Props = {
  initialPayload: string
  onChangePayload: (config: z.infer<typeof WorkflowActionInConversationMessageConfigSchema>) => void
  eventPayload: Record<string, unknown>
  eventName: string
  nodeIndex: number
  channel: z.infer<typeof UpdateCampaignSchema>["channel"]
}

export function EditInConversationMessagePayloadDialog({
  initialPayload,
  onChangePayload,
  eventPayload,
  eventName,
  nodeIndex,
  channel,
}: Props) {
  const { t } = useTranslation()
  const { form } = useWorkflowBuilder()
  const [payload, setPayload] = useState(initialPayload)
  const [locale, setLocale] = useState<string | undefined>()
  const [isOpen, setIsOpen] = useState(false)
  const previewContext = useVaultPreviewContext(eventPayload)

  const renderedPayload = useMemo(() => {
    return renderLiquid(payload?.trim() ?? "", previewContext)
  }, [previewContext, payload])

  useEffect(() => {
    if (isOpen) {
      setPayload(form.watch(`nodes.${nodeIndex}.config.message`))
      setLocale(form.getValues(`nodes.${nodeIndex}.config.locale`))
    }
  }, [form, isOpen, nodeIndex])

  const isValidPayload = !!renderedPayload
  const isDirty =
    form.getValues(`nodes.${nodeIndex}.config.message`) !== payload ||
    form.getValues(`nodes.${nodeIndex}.config.locale`) !== locale

  const lastValidPayloadRef = useRef(renderedPayload)
  if (isValidPayload) lastValidPayloadRef.current = renderedPayload

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="w-fit">
          <PencilIcon className="size-4" />
          {t("campaigns.action.availableActions.SendInConversationMessage.config.editPayload")}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-screen w-full sm:w-[calc(100vw-2rem)] sm:max-w-4xl flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {t("campaigns.action.availableActions.SendInConversationMessage.config.editPayload")}
          </DialogTitle>
          <DialogDescription className="flex flex-col gap-2 items-start">
            <span>
              <Trans
                i18nKey="campaigns.action.availableActions.SendInConversationMessage.config.editPayloadDescription"
                components={{
                  b: <span className="font-medium" />,
                  a: (
                    <a
                      href="https://shopify.dev/docs/api/liquid"
                      target="_blank"
                      rel="noreferrer"
                      className="underline hover:opacity-80"
                    />
                  ),
                }}
              />
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="flex md:flex-row flex-col gap-4 min-h-0 flex-1 overflow-auto">
          <div className="flex flex-col gap-4 flex-1 min-w-0">
            <div className="flex flex-col gap-2">
              <Label htmlFor={`workflow-message-locale-${nodeIndex}`}>
                {t("campaigns.action.availableActions.SendInConversationMessage.config.locale")}
              </Label>
              <Select value={locale ?? ""} onValueChange={setLocale}>
                <SelectTrigger id={`workflow-message-locale-${nodeIndex}`}>
                  <SelectValue
                    placeholder={t(
                      "campaigns.action.availableActions.SendInConversationMessage.config.localePlaceholder",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  {whatsappLanguages.map(({ label, value }) => (
                    <SelectItem key={value} value={value}>
                      {label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-2 flex-1">
              <Label>
                {t("campaigns.action.availableActions.SendInConversationMessage.config.template")}
              </Label>
              <VaultVariableAutocomplete variables={[]} className="flex min-h-0 flex-1">
                <Textarea
                  value={payload}
                  placeholder={t(
                    "campaigns.action.availableActions.SendInConversationMessage.config.payloadPlaceholder",
                  )}
                  onKeyDown={(e) => {
                    // Allow Enter key for line breaks, prevent form submission
                    if (e.key === "Enter") {
                      e.stopPropagation()
                    }
                  }}
                  onChange={(e) => {
                    setPayload(e.target.value)
                  }}
                  className="font-mono min-h-0 flex-1 resize-none"
                  style={{ tabSize: 2 }}
                />
              </VaultVariableAutocomplete>
              <Button
                variant="outline"
                size="sm"
                className="w-fit"
                onClick={() => setPayload(CAMPAIGN_ACTION_WEBHOOK_DEFAULT_PAYLOAD)}
              >
                <Braces className="size-4" />
                {t(
                  "campaigns.action.availableActions.SendInConversationMessage.config.useDefaultPayload",
                )}
              </Button>
            </div>
            <div className="flex flex-col gap-1">
              <Label className="flex items-center gap-1">
                <span>
                  {t("campaigns.action.availableActions.SendInConversationMessage.config.data")}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <IconInfoCircle className="size-4" />
                  </TooltipTrigger>
                  <TooltipContent>
                    <Trans
                      i18nKey="campaigns.action.availableActions.SendInConversationMessage.config.dataTooltip"
                      components={{
                        b: <span className="font-medium" />,
                      }}
                      values={{ eventName }}
                    />
                  </TooltipContent>
                </Tooltip>
              </Label>
              <MarkdownRenderer
                className="bg-muted rounded-md border max-h-[300px] overflow-auto"
                codeClassName="border-none"
              >
                {`\`\`\`json\n${JSON.stringify(eventPayload, null, 2)}\n\`\`\``}
              </MarkdownRenderer>
            </div>
          </div>
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <Label>
              {t("campaigns.action.availableActions.SendInConversationMessage.config.preview")}
            </Label>
            {channel === ChannelEnum.Whatsapp ? (
              <WhatsappTemplatePreview body={lastValidPayloadRef.current ?? ""} />
            ) : (
              <div>{lastValidPayloadRef.current ?? ""}</div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            {t("shared.cancel")}
          </Button>
          <Button
            onClick={() => {
              if (!locale) return
              onChangePayload({ message: payload, locale })
              setIsOpen(false)
            }}
            disabled={!isValidPayload || !locale || !isDirty}
          >
            {t("shared.confirm")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
